import {
  ExternalLink,
  Eye,
  Heart,
  Share2,
  MessageCircle,
  Users,
  Globe,
  Tag,
  Clock,
  PlayCircle,
  TrendingUp,
  Hash,
} from "lucide-react";

import { cn } from "../../../lib/utils";
import { parseUTCDate } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

// ---------- helpers (kept local to avoid coupling to manager) ----------

const formatCount = (n) => {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(num);
};

const formatPercent = (rate) => `${((Number(rate) || 0) * 100).toFixed(1)}%`;

const platformColor = (p) => {
  switch (p) {
    case "tiktok":
      return "bg-pink-500/15 text-pink-500 border-pink-500/20";
    case "instagram":
      return "bg-purple-500/15 text-purple-500 border-purple-500/20";
    case "facebook":
      return "bg-blue-500/15 text-blue-500 border-blue-500/20";
    case "youtube":
      return "bg-red-500/15 text-red-500 border-red-500/20";
    default:
      return "bg-gray-500/15 text-gray-500 border-gray-500/20";
  }
};

const formatDuration = (secs) => {
  const n = Number(secs);
  if (!n || n <= 0) return null;
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

// ---------- component ----------

const VideoDetailDialog = ({ open, onOpenChange, video, isDark }) => {
  if (!video) return null;

  const publishedDate = video.published_at
    ? parseUTCDate(video.published_at)
    : null;
  const followers = Number(video.account_followers) || 0;
  const duration = formatDuration(video.duration_seconds);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        {/* ===== Hero thumbnail ===== */}
        <div
          className={cn(
            "relative w-full aspect-video bg-gradient-to-br",
            isDark
              ? "from-gray-900 to-black"
              : "from-gray-100 to-gray-200"
          )}
        >
          {video.thumbnail_url ? (
            <img
              src={video.thumbnail_url}
              alt={video.hook_text || "video"}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <PlayCircle
                className={cn(
                  "h-16 w-16 opacity-30",
                  isDark ? "text-white" : "text-gray-400"
                )}
              />
            </div>
          )}

          <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap">
            <span
              className={cn(
                "px-2 py-0.5 rounded text-[11px] font-semibold border backdrop-blur-md capitalize",
                platformColor(video.platform)
              )}
            >
              {video.platform}
            </span>
            {video.sector && (
              <span
                className={cn(
                  "px-2 py-0.5 rounded text-[11px] font-medium border backdrop-blur-md flex items-center gap-1",
                  isDark
                    ? "bg-black/50 text-white border-white/20"
                    : "bg-white/80 text-gray-700 border-white"
                )}
              >
                <Tag className="h-2.5 w-2.5" />
                {video.sector}
              </span>
            )}
            {video.country && (
              <span
                className={cn(
                  "px-2 py-0.5 rounded text-[11px] font-medium border backdrop-blur-md flex items-center gap-1",
                  isDark
                    ? "bg-black/50 text-white border-white/20"
                    : "bg-white/80 text-gray-700 border-white"
                )}
              >
                <Globe className="h-2.5 w-2.5" />
                {video.country}
              </span>
            )}
          </div>

          {duration && (
            <span
              className={cn(
                "absolute bottom-3 right-3 px-2 py-0.5 rounded text-[11px] font-medium backdrop-blur-md",
                "bg-black/60 text-white border border-white/10"
              )}
            >
              {duration}
            </span>
          )}
        </div>

        <div className="px-5 pb-5 pt-3 space-y-4">
          <DialogHeader className="space-y-1">
            <DialogTitle
              className={cn(
                "text-[16px] font-semibold leading-snug",
                isDark ? "text-white" : "text-gray-900"
              )}
            >
              {video.hook_text ||
                video.caption?.slice(0, 120) ||
                "Video details"}
            </DialogTitle>
            {video.author_handle && (
              <p
                className={cn(
                  "text-[12px]",
                  isDark ? "text-gray-500" : "text-gray-500"
                )}
              >
                {video.author_handle}
              </p>
            )}
          </DialogHeader>

          {/* ===== Open original ===== */}
          <div className="flex flex-wrap items-center gap-2">
            {video.url && (
              <Button
                size="sm"
                asChild
                className="gap-1.5"
              >
                <a href={video.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" />
                  Bekijk origineel
                </a>
              </Button>
            )}
            {publishedDate && (
              <Badge variant="outline" className="gap-1 h-7 px-2 text-[12px]">
                <Clock className="h-3 w-3" />
                {publishedDate.toLocaleDateString("nl-BE", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </Badge>
            )}
          </div>

          {/* ===== Metrics grid ===== */}
          <div
            className={cn(
              "grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-xl border p-3",
              isDark
                ? "bg-white/[0.02] border-white/[0.06]"
                : "bg-gray-50/50 border-gray-200/70"
            )}
          >
            <MetricTile
              icon={Eye}
              label="Views"
              value={formatCount(video.views)}
              isDark={isDark}
            />
            <MetricTile
              icon={Heart}
              label="Likes"
              value={formatCount(video.likes)}
              isDark={isDark}
            />
            <MetricTile
              icon={Share2}
              label="Shares"
              value={formatCount(video.shares)}
              isDark={isDark}
            />
            <MetricTile
              icon={MessageCircle}
              label="Comments"
              value={formatCount(video.comments)}
              isDark={isDark}
            />
          </div>

          <div
            className={cn(
              "grid grid-cols-2 gap-2 rounded-xl border p-3",
              isDark
                ? "bg-white/[0.02] border-white/[0.06]"
                : "bg-gray-50/50 border-gray-200/70"
            )}
          >
            <MetricTile
              icon={TrendingUp}
              label="Engagement rate"
              value={formatPercent(video.engagement_rate)}
              accent={isDark ? "text-emerald-400" : "text-emerald-600"}
              isDark={isDark}
            />
            <MetricTile
              icon={Users}
              label="Volgers"
              value={followers > 0 ? formatCount(followers) : "–"}
              isDark={isDark}
            />
          </div>

          {/* ===== Hook ===== */}
          {video.hook_text && (
            <Section title="Hook" isDark={isDark} icon={Hash}>
              <p
                className={cn(
                  "text-[13px] leading-relaxed",
                  isDark ? "text-gray-200" : "text-gray-800"
                )}
              >
                {video.hook_text}
              </p>
            </Section>
          )}

          {/* ===== Caption ===== */}
          {video.caption && (
            <Section title="Caption" isDark={isDark}>
              <p
                className={cn(
                  "text-[13px] leading-relaxed whitespace-pre-wrap",
                  isDark ? "text-gray-300" : "text-gray-700"
                )}
              >
                {video.caption}
              </p>
            </Section>
          )}

          {/* ===== Transcript ===== */}
          {video.transcript && (
            <Section title="Transcript" isDark={isDark}>
              <p
                className={cn(
                  "text-[12px] leading-relaxed whitespace-pre-wrap",
                  isDark ? "text-gray-400" : "text-gray-600"
                )}
              >
                {video.transcript}
              </p>
            </Section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MetricTile = ({ icon: Icon, label, value, isDark, accent }) => (
  <div className="flex items-center gap-2">
    <div
      className={cn(
        "h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0",
        isDark ? "bg-white/[0.06]" : "bg-white"
      )}
    >
      <Icon
        className={cn("h-3.5 w-3.5", isDark ? "text-gray-300" : "text-gray-600")}
      />
    </div>
    <div className="min-w-0">
      <p
        className={cn(
          "text-[10px] uppercase tracking-wider font-semibold",
          isDark ? "text-gray-500" : "text-gray-400"
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          "text-[14px] font-semibold truncate",
          accent || (isDark ? "text-white" : "text-gray-900")
        )}
      >
        {value}
      </p>
    </div>
  </div>
);

const Section = ({ title, isDark, icon: Icon, children }) => (
  <div>
    <p
      className={cn(
        "text-[11px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1",
        isDark ? "text-gray-400" : "text-gray-600"
      )}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {title}
    </p>
    {children}
  </div>
);

export default VideoDetailDialog;
